import type { ScopeQuery, ShipmentStatus } from "@app/shared"
import { z } from "zod"
import { http } from "../lib/http"

/**
 * Shipment status -- kept as a string so this client doesn't have
 * to track the engine's drizzle enum literally. The list view and
 * detail view treat it as an opaque label; if/when we need
 * exhaustive narrowing we can tighten this to a literal union.
 */
export const ShipmentStatusSchema = z.string()

/**
 * Mirrors `ShipmentSummaryDto` in `apps/engine/src/shipments/dto.ts`.
 *
 * `tenantId` is intentionally omitted server-side (every request is
 * already tenant-scoped via the session, so re-emitting it would be
 * noise and a small information-leak surface), so it's omitted here.
 */
export const ShipmentSummarySchema = z.object({
	id: z.string(),
	status: ShipmentStatusSchema,
	carrierId: z.string(),
	carrierName: z.string().nullable(),
	laneId: z.string(),
	bookingNumber: z.string().nullable(),
	blNumber: z.string().nullable(),
	originUnlocode: z.string().nullable(),
	destinationUnlocode: z.string().nullable(),
	vesselName: z.string().nullable(),
	voyageNumber: z.string().nullable(),
	etd: z.string().nullable(),
	eta: z.string().nullable(),
})

/**
 * Mirrors `ShipmentDetailDto`: summary + free-time + audit
 * timestamps.
 */
export const ShipmentDetailSchema = ShipmentSummarySchema.extend({
	freeTimeDays: z.number().nullable(),
	/** Marks stencilled on the bags — the register's "Bag Marking". */
	bagMarking: z.string().nullable(),
	createdAt: z.string(),
	updatedAt: z.string(),
})

const ShipmentListSchema = z.array(ShipmentSummarySchema)

export type ShipmentSummary = z.infer<typeof ShipmentSummarySchema>
export type ShipmentDetail = z.infer<typeof ShipmentDetailSchema>

/**
 * GET /shipments -- latest 100 for the session's tenant, newest first.
 *
 * Optional `q` is a case-insensitive substring filter against
 * `bookingNumber`, `blNumber`, and `id`. Empty/whitespace `q` = no
 * filter (same as omitting it). Optional `scope` narrows the list to the
 * app-wide country × vertical filter; `q` + scope are combined into a
 * single `?q=…&country=…&vertical=…` query string.
 */
export const listShipments = async (
	options: { q?: string; scope?: ScopeQuery; signal?: AbortSignal } = {},
): Promise<ShipmentSummary[]> => {
	const trimmed = options.q?.trim() ?? ""
	const search = new URLSearchParams()
	if (trimmed.length > 0) search.set("q", trimmed)
	if (options.scope?.country) search.set("country", options.scope.country)
	if (options.scope?.vertical) search.set("vertical", options.scope.vertical)
	const qs = search.toString()
	const path = qs ? `/shipments?${qs}` : "/shipments"
	const raw = await http<unknown>(
		path,
		options.signal !== undefined ? { signal: options.signal } : {},
	)
	return ShipmentListSchema.parse(raw)
}

/** GET /shipments/:id -- 404 surfaces as HttpError(status=404). */
export const getShipment = async (id: string, signal?: AbortSignal): Promise<ShipmentDetail> => {
	const raw = await http<unknown>(
		`/shipments/${encodeURIComponent(id)}`,
		signal !== undefined ? { signal } : {},
	)
	return ShipmentDetailSchema.parse(raw)
}

/**
 * Body for `POST /shipments` -- mirrors `CreateShipmentInput` in
 * `apps/engine/src/shipments/dto.ts`.
 *
 * `verticalId` + `originCountry` are required: the columns are NOT NULL
 * since migration 0014 and the app-wide scope filter reads them
 * directly. `status` is deliberately absent -- the engine starts every
 * new shipment at DRAFT.
 */
export interface CreateShipmentInput {
	carrierId: string
	laneId: string
	verticalId: string
	originCountry: string
	bookingNumber?: string | null
	blNumber?: string | null
	originUnlocode?: string | null
	destinationUnlocode?: string | null
	vesselName?: string | null
	voyageNumber?: string | null
	etd?: string | null
	eta?: string | null
	freeTimeDays?: number | null
	bagMarking?: string | null
}

/** POST /shipments -- 201 with the created detail row. 400 on a rejected scope pair. */
export const createShipment = async (input: CreateShipmentInput): Promise<ShipmentDetail> => {
	const raw = await http<unknown>("/shipments", { method: "POST", json: input })
	return ShipmentDetailSchema.parse(raw)
}

/**
 * Body for `PATCH /shipments/:id` -- send only what changed.
 *
 * `null` clears a field; omitting it leaves it alone. `status` is absent on
 * purpose: it moves via `setShipmentStatus`, which the engine gates on the
 * state machine.
 */
export interface UpdateShipmentInput {
	carrierId?: string
	laneId?: string
	verticalId?: string
	originCountry?: string
	bookingNumber?: string | null
	blNumber?: string | null
	originUnlocode?: string | null
	destinationUnlocode?: string | null
	vesselName?: string | null
	voyageNumber?: string | null
	etd?: string | null
	eta?: string | null
	freeTimeDays?: number | null
	bagMarking?: string | null
}

/** PATCH /shipments/:id -- correct a shipment's details. */
export const updateShipment = async (
	id: string,
	input: UpdateShipmentInput,
): Promise<ShipmentDetail> => {
	const raw = await http<unknown>(`/shipments/${encodeURIComponent(id)}`, {
		method: "PATCH",
		json: input,
	})
	return ShipmentDetailSchema.parse(raw)
}

/**
 * PATCH /shipments/:id/status -- move through the state machine.
 *
 * An illegal transition comes back as 409 (HttpError.status === 409) with the
 * engine's reason in the body; the UI surfaces it rather than guessing.
 */
export const setShipmentStatus = async (
	id: string,
	status: ShipmentStatus,
): Promise<ShipmentDetail> => {
	const raw = await http<unknown>(`/shipments/${encodeURIComponent(id)}/status`, {
		method: "PATCH",
		json: { status },
	})
	return ShipmentDetailSchema.parse(raw)
}

/**
 * GET /shipments/export -- CSV text for the current search + scope.
 *
 * Returns the raw document rather than triggering the download itself, so
 * the caller owns the browser-specific part and this stays testable.
 * Requires the `shipments.export` permission; a user without it gets a 403
 * surfaced as `HttpError`.
 */
export const exportShipmentsCsv = async (
	options: { q?: string; scope?: ScopeQuery; signal?: AbortSignal } = {},
): Promise<string> => {
	const trimmed = options.q?.trim() ?? ""
	const search = new URLSearchParams()
	if (trimmed.length > 0) search.set("q", trimmed)
	if (options.scope?.country) search.set("country", options.scope.country)
	if (options.scope?.vertical) search.set("vertical", options.scope.vertical)
	const qs = search.toString()
	const path = qs ? `/shipments/export?${qs}` : "/shipments/export"
	return http<string>(path, {
		headers: { Accept: "text/csv" },
		...(options.signal !== undefined ? { signal: options.signal } : {}),
	})
}

/**
 * React Query keys for shipments. Centralised here so list/detail
 * invalidations from mutations land later stay consistent.
 */
export const shipmentsQueryKeys = {
	all: ["shipments"] as const,
	// Scope segments are appended only when set, so `list()` stays a valid
	// invalidation prefix for every scoped/q-filtered variant.
	list: (q?: string, scope?: ScopeQuery): readonly string[] => {
		const trimmed = q?.trim() ?? ""
		const key: string[] = [...shipmentsQueryKeys.all, "list"]
		if (trimmed.length > 0) key.push("q", trimmed)
		if (scope?.country) key.push(scope.country)
		if (scope?.vertical) key.push(scope.vertical)
		return key
	},
	detail: (id: string) => [...shipmentsQueryKeys.all, "detail", id] as const,
}
